import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  IconButton, 
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  InputAdornment,
  Switch,
  FormControlLabel,
  Snackbar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import { 
  getUsers, 
  createUser, 
  updateUser, 
  deleteUser,
  CreateUserData
} from '../services/userService';
import { getRoles } from '../services/roleService';
import { IUser, IRole } from '../types/damTypes';
import ImportUsersDialog from '../components/ImportUsersDialog';
import axiosInstance from '../services/axiosInstance';

type UserFormData = CreateUserData & { isActive?: boolean };

const UserManagementPage: React.FC = () => {
  // State
  const [users, setUsers] = useState<IUser[]>([]);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<string>('');
  
  // Modal state
  const [isUserModalOpen, setIsUserModalOpen] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<IUser | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    roles: [],
    isActive: true
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [passwordWasEmailed, setPasswordWasEmailed] = useState<boolean>(false);
  const [resetPassword, setResetPassword] = useState<string>('');
  const [sendResetEmail, setSendResetEmail] = useState<boolean>(false);
  
  // Notification state
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);
  
  // Import dialog state
  const [isImportDialogOpen, setIsImportDialogOpen] = useState<boolean>(false);
  const [sendingInvite, setSendingInvite] = useState<string | null>(null);

  // Load users and roles data
  const fetchUsersAndRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersResponse, rolesResponse] = await Promise.all([
        getUsers(),
        getRoles()
      ]);

      if (usersResponse.data) {
        setUsers(usersResponse.data);
      } else {
        setError(usersResponse.error || 'Kunde inte hämta användare');
      }

      if (rolesResponse.data) {
        setRoles(rolesResponse.data);
      } else {
        // Not showing error for roles, just logging it
        console.error('Kunde inte hämta roller:', rolesResponse.error);
      }

    } catch (err: any) {
      console.error("Fel vid hämtning av användare eller roller:", err);
      setError(err.message || 'Ett oväntat fel inträffade');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsersAndRoles();
  }, []);

  // Helper function to generate a random password
  const generateRandomPassword = (): string => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_-+=";
    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }
    return password;
  };

  // Form handlers
  const handleOpenUserModal = (user?: IUser) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '', // Don't set password when editing
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        roles: Array.isArray(user.roles) 
          ? user.roles.map(role => typeof role === 'string' ? role : role._id)
          : [],
        isActive: user.isActive
      });
      setResetPassword('');
      setSendResetEmail(false);
    } else {
      const randomPassword = generateRandomPassword();
      setEditingUser(null);
      setFormData({
        email: '',
        password: randomPassword,
        firstName: '',
        lastName: '',
        roles: [],
        isActive: true
      });
      setShowPassword(true);
    }
    setPasswordWasEmailed(false);
    setIsUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setIsUserModalOpen(false);
    setEditingUser(null);
    setShowPassword(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRolesChange = (event: any) => {
    const selectedRoles = event.target.value as string[];
    setFormData(prev => ({ ...prev, roles: selectedRoles }));
  };

  const handleRoleFilterChange = (event: any) => {
    setSelectedRoleFilter(event.target.value as string);
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const regeneratePassword = () => {
    const newPassword = generateRandomPassword();
    setFormData(prev => ({ ...prev, password: newPassword }));
    setShowPassword(true);
    setPasswordWasEmailed(false);
  };

  // Simulate sending an email with the password
  const simulateEmailPassword = () => {
    // In a real application, you would call an API to send an email
    console.log(`Simulating email to ${formData.email} with password: ${formData.password}`);
    setPasswordWasEmailed(true);
    
    // Show success notification
    setSnackbarMessage(`Lösenord skickat till ${formData.email}`);
    setIsSuccess(true);
    setSnackbarOpen(true);
  };

  // Save user (create or update)
  const handleSaveUser = async () => {
    setLoading(true);
    try {
      if (editingUser) {
        // Update existing user
        const userData = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          roles: formData.roles,
          isActive: formData.isActive !== false
        };
        
        const response = await updateUser(editingUser._id, userData);
        if (response.error) {
          throw new Error(response.error);
        }
        
        // Show success notification
        setSnackbarMessage('Användaren har uppdaterats');
        setIsSuccess(true);
      } else {
        // Create new user
        const response = await createUser({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          roles: formData.roles
        });
        if (response.error) {
          throw new Error(response.error);
        }
        
        // If admin chose to email password, send it now via reset-password (sets mustChangePassword)
        if (passwordWasEmailed && response.data?._id) {
          try {
            const { resetUserPassword } = await import('../services/userService');
            await resetUserPassword(response.data._id, formData.password, true);
          } catch (e) {
            console.error('Kunde inte skicka lösenord via e-post:', e);
          }
        }

        // Show success notification
        setSnackbarMessage('Ny användare har skapats');
        setIsSuccess(true);
      }
      
      // Refresh data and close modal
      fetchUsersAndRoles();
      handleCloseUserModal();
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error('Error saving user:', err);
      setSnackbarMessage(err.message || 'Ett fel uppstod vid sparande av användare');
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Är du säker på att du vill radera denna användare?')) {
      setLoading(true);
      try {
        const response = await deleteUser(userId);
        if (response.error) {
          throw new Error(response.error);
        }
        
        // Show success notification
        setSnackbarMessage('Användaren har raderats');
        setIsSuccess(true);
        setSnackbarOpen(true);
        
        // Refresh data
        fetchUsersAndRoles();
      } catch (err: any) {
        console.error('Error deleting user:', err);
        setSnackbarMessage(err.message || 'Ett fel uppstod vid radering av användare');
        setIsSuccess(false);
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    }
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Aldrig';
    return new Date(dateString).toLocaleString('sv-SE');
  };

  // Render role names
  const renderRoles = (userRoles: (string | IRole)[]) => {
    return userRoles.map(role => {
      const roleName = typeof role === 'string' 
        ? roles.find(r => r._id === role)?.name || role
        : role.name;
      
      return (
        <Chip 
          key={typeof role === 'string' ? role : role._id} 
          label={roleName} 
          size="small" 
          sx={{ mr: 0.5, mb: 0.5 }} 
        />
      );
    });
  };

  const filteredUsers = useMemo(() => {
    if (!selectedRoleFilter) return users;

    const selectedRoleName = roles.find(role => role._id === selectedRoleFilter)?.name?.toLowerCase();

    return users.filter(user =>
      Array.isArray(user.roles) &&
      user.roles.some(role => {
        if (typeof role === 'string') {
          return role === selectedRoleFilter || (!!selectedRoleName && role.toLowerCase() === selectedRoleName);
        }

        return role._id === selectedRoleFilter || (!!selectedRoleName && role.name.toLowerCase() === selectedRoleName);
      })
    );
  }, [users, roles, selectedRoleFilter]);

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Export users as CSV
  const handleExportUsers = async () => {
    try {
      const response = await axiosInstance.get('/api/users/export', {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setSnackbarMessage('Kunde inte exportera användare');
      setIsSuccess(false);
      setSnackbarOpen(true);
    }
  };

  // Send invitation email to a user
  const handleSendInvitation = async (userId: string) => {
    setSendingInvite(userId);
    try {
      const response = await axiosInstance.post(`/api/users/${userId}/invite`);
      setSnackbarMessage(response.data.message || 'Inbjudan skickad');
      setIsSuccess(true);
      setSnackbarOpen(true);
    } catch (err: any) {
      setSnackbarMessage(err.response?.data?.message || 'Kunde inte skicka inbjudan');
      setIsSuccess(false);
      setSnackbarOpen(true);
    } finally {
      setSendingInvite(null);
    }
  };

  // Main render
  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Användare</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            onClick={() => setIsImportDialogOpen(true)} 
            startIcon={<UploadIcon />}
            size="small"
          >
            Importera
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleExportUsers} 
            startIcon={<DownloadIcon />}
            size="small"
          >
            Exportera
          </Button>
          <Button 
            variant="contained" 
            onClick={() => handleOpenUserModal()} 
            startIcon={<PersonAddIcon />}
          >
            Lägg till användare
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="user-role-filter-label">Filtrera på roll</InputLabel>
          <Select
            labelId="user-role-filter-label"
            value={selectedRoleFilter}
            label="Filtrera på roll"
            onChange={handleRoleFilterChange}
          >
            <MenuItem value="">Alla roller</MenuItem>
            {roles.map((role) => (
              <MenuItem key={role._id} value={role._id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {filteredUsers.length === 0 && !loading ? (
        <Typography>
          {users.length === 0 ? 'Inga användare hittades.' : 'Inga användare matchar valt rollfilter.'}
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="users table">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Namn</TableCell>
                <TableCell>Roller</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Senast inloggad</TableCell>
                <TableCell align="right">Åtgärder</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.firstName} {user.lastName}
                  </TableCell>
                  <TableCell>
                    {renderRoles(user.roles)}
                  </TableCell>
                  <TableCell>
                    {user.isActive ? 'Aktiv' : 'Inaktiv'}
                  </TableCell>
                  <TableCell>
                    {formatDate(user.lastLoginAt)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Skicka inbjudan">
                      <IconButton 
                        onClick={() => handleSendInvitation(user._id)} 
                        size="small" 
                        sx={{ mr: 1 }}
                        disabled={sendingInvite === user._id}
                        color="primary"
                      >
                        {sendingInvite === user._id ? (
                          <CircularProgress size={18} />
                        ) : (
                          <SendIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Redigera användare">
                      <IconButton 
                        onClick={() => handleOpenUserModal(user)} 
                        size="small" 
                        sx={{ mr: 1 }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Radera användare">
                      <IconButton 
                        onClick={() => handleDeleteUser(user._id)} 
                        size="small" 
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* User Create/Edit Modal */}
      <Dialog 
        open={isUserModalOpen} 
        onClose={handleCloseUserModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingUser ? 'Redigera Användare' : 'Lägg till Användare'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              required
              type="email"
            />
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Förnamn"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="Efternamn"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                fullWidth
              />
            </Box>
            
            <FormControl fullWidth>
              <InputLabel id="roles-label">Roller</InputLabel>
              <Select
                labelId="roles-label"
                multiple
                value={formData.roles || []}
                onChange={handleRolesChange}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((roleId) => {
                      const role = roles.find(r => r._id === roleId);
                      return (
                        <Chip 
                          key={roleId} 
                          label={role?.name || roleId}
                          onDelete={() => {
                            // Remove this role from the selected roles
                            const updatedRoles = formData.roles ? formData.roles.filter(id => id !== roleId) : [];
                            setFormData(prev => ({ ...prev, roles: updatedRoles }));
                          }}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {roles.map((role) => (
                  <MenuItem key={role._id} value={role._id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Välj roller för användaren</FormHelperText>
            </FormControl>
            
            {!editingUser && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Lösenord
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="Lösenord"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    fullWidth
                    required={!editingUser}
                    type={showPassword ? 'text' : 'password'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={toggleShowPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Tooltip title="Generera nytt lösenord">
                    <IconButton onClick={regeneratePassword}>
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Skicka lösenord via e-post">
                    <span>
                      <IconButton 
                        onClick={simulateEmailPassword}
                        disabled={!formData.email || !formData.password}
                        color={passwordWasEmailed ? "success" : "primary"}
                      >
                        <EmailIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                {passwordWasEmailed && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Lösenord har skickats till {formData.email}
                  </Alert>
                )}
              </Box>
            )}
            
            {editingUser && (
              <>
                <FormControlLabel
                  control={<Switch checked={formData.isActive !== false} onChange={(e)=> setFormData(prev=> ({...prev, isActive: e.target.checked}))} name="isActive" />}
                  label="Aktiv användare"
                />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Återställ lösenord (valfritt)
                  </Typography>
                  <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
                    <TextField
                      label="Nytt lösenord"
                      value={resetPassword}
                      onChange={(e)=> setResetPassword(e.target.value)}
                      type={showPassword ? 'text' : 'password'}
                      fullWidth
                    />
                    <Tooltip title="Generera nytt lösenord">
                      <IconButton onClick={()=> setResetPassword(generateRandomPassword())}>
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                    <FormControlLabel control={<Switch checked={sendResetEmail} onChange={(e)=> setSendResetEmail(e.target.checked)} />} label="Skicka via e‑post" />
                    <Button variant="outlined" disabled={!resetPassword} onClick={async ()=>{
                      if (!editingUser) return;
                      setLoading(true);
                      try {
                        const { resetUserPassword } = await import('../services/userService');
                        const resp = await resetUserPassword(editingUser._id, resetPassword, sendResetEmail);
                        if (resp.error) throw new Error(resp.error);
                        setSnackbarMessage(sendResetEmail ? 'Lösenord återställt och skickat via e‑post' : 'Lösenord återställt');
                        setIsSuccess(true);
                        setSnackbarOpen(true);
                        setResetPassword('');
                        setSendResetEmail(false);
                      } catch (err:any) {
                        setSnackbarMessage(err.message || 'Kunde inte återställa lösenord');
                        setIsSuccess(false);
                        setSnackbarOpen(true);
                      } finally { setLoading(false); }
                    }}>Spara nytt lösenord</Button>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserModal}>Avbryt</Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained"
            disabled={!formData.email || (!editingUser && !formData.password)}
          >
            {editingUser ? 'Spara ändringar' : 'Skapa användare'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={isSuccess ? "success" : "error"}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* Import Users Dialog */}
      <ImportUsersDialog
        open={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        onImportComplete={fetchUsersAndRoles}
        availableRoles={roles.map((role) => role.name).sort((a, b) => a.localeCompare(b, 'sv'))}
      />
    </Box>
  );
};

export default UserManagementPage; 